import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Check, Minus } from 'lucide-react';
import { SubscriptionTier, TierPreview } from '../../services/subscriptionApi';

interface FeatureComparisonAccordionProps {
  tiers: SubscriptionTier[];
  selectedTierId?: number;
  isLifetime?: boolean;
  tierPreviews?: Record<number, TierPreview>;
lifetimeStackedValues?: { named_users: number; public_view_users: number; api_endpoints?: number; custom_domains?: number; branding_policies?: number; };
}

const FEATURE_CATEGORIES = [
  {
    name: 'Usage Limits',
    features: [
      { key: 'base_named_users', label: 'Monthly Active Users' },
      { key: 'base_public_view_users', label: 'Public View Report Licenses' },
      { key: 'base_api_endpoints', label: 'API Endpoints' },
      { key: 'base_scheduled_jobs', label: 'Concurrent Scheduled Jobs' },
      { key: 'base_custom_domains', label: 'Custom Domains' },
      { key: 'base_branding_policies', label: 'Branding Policies' },
    ],
  },
  {
    name: 'Data Connectivity',
    features: [
      { key: 'license_121', label: 'Static Egress IP' },
      { key: 'license_122', label: 'Native Connections' },
      { key: 'base_odbc_tier1_drivers', label: 'ODBC Drivers (Tier 1)' },
      { key: 'license_119', label: 'ODBC Drivers (Tier 2 — Custom Uploads)' },
      { key: 'license_118', label: 'TLS Certificate Management' },
      { key: 'license_120', label: 'SSH Tunnel' },
    ],
  },
  {
    name: 'Core Features',
    features: [
      { key: 'license_100', label: 'Data Tags & Row-Level Security' },
      { key: 'license_101', label: 'Raw SQL Queries' },
      { key: 'license_102', label: 'Email Scheduling' },
      { key: 'license_103', label: 'Report Embedding' },
      { key: 'license_104', label: 'API Access' },
      { key: 'license_105', label: 'Login Page Branding' },
    ],
  },
  {
    name: 'Advanced Features',
    features: [
      { key: 'license_110', label: 'Stored Procedures' },
      { key: 'license_111', label: 'Phrase Manager' },
      { key: 'license_112', label: 'FastCache (Dashboard Caching)' },
      { key: 'license_113', label: 'Server Sync' },
      { key: 'license_114', label: 'Cisco DUO 2FA' },
      { key: 'license_115', label: 'Per-Tenant Datasource' },
      { key: 'license_116', label: 'SSO' },
    ],
  },
  {
    name: 'Administration & Support',
    features: [
      { key: 'audit_log_retention_days', label: 'Audit Log Retention' },
      { key: 'support_level', label: 'Support' },
    ],
  },
];

const formatValue = (
  tier: SubscriptionTier,
  key: string,
  isLifetime?: boolean,
  tierPreviews?: Record<number, TierPreview>,
lifetimeStackedValues?: { named_users: number; public_view_users: number; api_endpoints?: number; custom_domains?: number; branding_policies?: number; }
): React.ReactNode => {
  // License features (boolean check/uncheck)
  if (key.startsWith('license_')) {
    const licenseId = key.replace('license_', '');
    const features =
      isLifetime && tierPreviews?.[tier.id]
        ? (tierPreviews[tier.id].features as Record<string, boolean>)
        : (tier.base_license_features as Record<string, boolean> | undefined);
    const hasFeature = features?.[licenseId] === true;

    return hasFeature ? (
      <span
        style={{
          width: '22px',
          height: '22px',
          borderRadius: '50%',
          backgroundColor: 'var(--v2-success-bg)',
          color: 'var(--v2-success-dark)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Check size={14} strokeWidth={2.5} />
      </span>
    ) : (
      <Minus size={18} strokeWidth={1.5} color="var(--v2-text-muted)" />
    );
  }

  const preview = isLifetime ? tierPreviews?.[tier.id] : undefined;
  const isLifetimeTier = tier.tier_code?.startsWith('lifetime_');

  const keyMap: Record<string, keyof TierPreview> = {
    base_named_users: 'named_users',
    base_public_view_users: 'public_view_users',
    base_api_endpoints: 'api_endpoints',
    base_custom_domains: 'custom_domains',
    base_branding_policies: 'branding_policies',
    base_odbc_tier1_drivers: 'odbc_tier1_drivers',
  };

  let displayValue: number | null | undefined;
  let lifetimeBonus: number | undefined;
  const tierBaseValue = (tier as any)[key];

if (isLifetime && preview && keyMap[key]) {
    if (isLifetimeTier && lifetimeStackedValues) {
      // For lifetime tier column show stacked values only, not tier base + stacked
      const stackedKey = keyMap[key] as keyof typeof lifetimeStackedValues;
      displayValue = lifetimeStackedValues[stackedKey] ?? tierBaseValue;
    } else {
      displayValue = preview[keyMap[key]] as number;
      if (!isLifetimeTier && preview.lifetime_bonus) {
        const bonusKey = keyMap[key].replace('base_', '') as keyof typeof preview.lifetime_bonus;
        lifetimeBonus = (preview.lifetime_bonus as Record<string, number>)?.[bonusKey];
      }
    }
  } else {
    displayValue = tierBaseValue;
  }

  if (displayValue === null || displayValue === undefined) {
    return <span style={{ color: 'var(--v2-text-tertiary)' }}>Custom</span>;
  }

  if (displayValue === -1 || displayValue >= 9999) {
    return <span style={{ color: 'var(--v2-success-dark)', fontWeight: 600 }}>Unlimited</span>;
  }

  if (key === 'audit_log_retention_days') {
    return `${displayValue} days`;
  }

  if (key === 'support_level') {
    return displayValue || 'Email + AI Help';
  }

  if (tierBaseValue === -1 || tierBaseValue >= 9999) {
    return <span style={{ color: 'var(--v2-success-dark)', fontWeight: 600 }}>Unlimited</span>;
  }

  if (lifetimeBonus && lifetimeBonus > 0 && !isLifetimeTier) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        <span>{displayValue}</span>
        <span style={{ color: 'var(--v2-success)', fontWeight: 600, fontSize: '0.75rem' }}>
          (+{lifetimeBonus})
        </span>
      </span>
    );
  }

  return displayValue.toString();
};

const FeatureComparisonAccordion: React.FC<FeatureComparisonAccordionProps> = ({
  tiers,
  selectedTierId,
  isLifetime = false,
  tierPreviews = {},
  lifetimeStackedValues,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [headerHovered, setHeaderHovered] = useState(false);

  if (tiers.length === 0) return null;

  return (
    <div
      style={{
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        border: '1px solid var(--v2-border)',
        backgroundColor: 'var(--v2-surface)',
      }}
    >
      {/* Accordion Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        onMouseEnter={() => setHeaderHovered(true)}
        onMouseLeave={() => setHeaderHovered(false)}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          cursor: 'pointer',
          backgroundColor: headerHovered ? 'var(--v2-surface-hover)' : 'var(--v2-surface-alt)',
          border: 'none',
          textAlign: 'left',
          transition: 'background-color 0.15s ease',
        }}
      >
        <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--v2-text)' }}>
          Compare All Features
        </span>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
            borderRadius: 'var(--radius-sm)',
            backgroundColor: expanded ? 'var(--v2-primary-light)' : 'transparent',
            color: expanded ? 'var(--v2-primary)' : 'var(--v2-text-muted)',
            transition: 'all 0.15s ease',
          }}
        >
          {expanded ? <ChevronUp size={18} strokeWidth={2} /> : <ChevronDown size={18} strokeWidth={2} />}
        </div>
      </button>

      {/* Accordion Content */}
      {expanded && (
        <div style={{ overflowX: 'auto', borderTop: '1px solid var(--v2-border)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead>
              <tr>
                <th
                  style={{
                    fontWeight: 500,
                    width: '30%',
                    padding: '12px 16px',
                    textAlign: 'left',
                    color: 'var(--v2-text-muted)',
                    backgroundColor: 'var(--v2-surface-alt)',
                    borderBottom: '1px solid var(--v2-border)',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                  }}
                >
                  Features
                </th>
                {tiers.map((tier) => (
                  <th
                    key={tier.id}
                    style={{
                      fontWeight: 600,
                      padding: '12px 16px',
                      textAlign: 'center',
                      color: selectedTierId === tier.id ? 'var(--v2-primary)' : 'var(--v2-text)',
                      backgroundColor: selectedTierId === tier.id ? 'var(--v2-primary-light)' : 'var(--v2-surface-alt)',
                      borderLeft: '1px solid var(--v2-border)',
                      borderBottom: '1px solid var(--v2-border)',
                      fontSize: '0.875rem',
                    }}
                  >
                    {tier.tier_name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FEATURE_CATEGORIES.map((category) => (
                <React.Fragment key={category.name}>
                  {/* Category Header */}
                  <tr>
                    <td
                      colSpan={tiers.length + 1}
                      style={{
                        backgroundColor: 'var(--v2-text)',
                        color: 'var(--v2-surface)',
                        fontWeight: 600,
                        fontSize: '0.6875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        padding: '8px 16px',
                      }}
                    >
                      {category.name}
                    </td>
                  </tr>

                  {/* Feature Rows */}
                  {category.features.map((feature) => (
                    <tr
                      key={feature.key}
                      onMouseEnter={() => setHoveredRow(feature.key)}
                      onMouseLeave={() => setHoveredRow(null)}
                      style={{
                        backgroundColor: hoveredRow === feature.key ? 'var(--v2-surface-hover)' : 'transparent',
                        transition: 'background-color 0.1s ease',
                      }}
                    >
                      <td
                        style={{
                          fontWeight: 400,
                          padding: '10px 16px',
                          color: 'var(--v2-text-secondary)',
                          borderBottom: '1px solid var(--v2-border-light)',
                        }}
                      >
                        {feature.label}
                      </td>
                      {tiers.map((tier) => (
                        <td
                          key={tier.id}
                          style={{
                            padding: '10px 16px',
                            textAlign: 'center',
                            fontWeight: 500,
                            color: 'var(--v2-text)',
                            backgroundColor: selectedTierId === tier.id ? 'rgba(79, 70, 229, 0.04)' : undefined,
                            borderLeft: '1px solid var(--v2-border-light)',
                            borderBottom: '1px solid var(--v2-border-light)',
                          }}
                        >
{formatValue(tier, feature.key, isLifetime, tierPreviews, lifetimeStackedValues)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FeatureComparisonAccordion;
